import { useState, useCallback } from 'react';
import { pwg208Api, FG160 } from '../../../services/pwg208Api';
import { message } from 'antd';

export function usePWG208Navigation(onLoad: (id: string) => Promise<void>) {
  const [allIds, setAllIds] = useState<string[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);

  const loadList = useCallback(async () => {
    try {
      const res = await pwg208Api.list(1, 1000);
      const ids = (res.data as FG160[]).map((r) => r.fg160_01);
      setAllIds(ids);
      if (ids.length > 0) {
        setCurrentIdx(ids.length - 1);
        await onLoad(ids[ids.length - 1]);
      }
    } catch {
      message.error('載入清單失敗');
    }
  }, [onLoad]);

  const navTo = useCallback(
    async (idx: number) => {
      if (idx < 0 || idx >= allIds.length) return;
      setCurrentIdx(idx);
      await onLoad(allIds[idx]);
    },
    [allIds, onLoad]
  );

  const navFirst = useCallback(() => navTo(0), [navTo]);
  const navPrev = useCallback(() => navTo(currentIdx - 1), [navTo, currentIdx]);
  const navNext = useCallback(() => navTo(currentIdx + 1), [navTo, currentIdx]);
  const navLast = useCallback(() => navTo(allIds.length - 1), [navTo, allIds.length]);

  const appendId = useCallback((id: string) => {
    setAllIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      setCurrentIdx(next.length - 1);
      return next;
    });
  }, []);

  const removeId = useCallback(
    (id: string) => {
      setAllIds((prev) => {
        const next = prev.filter((x) => x !== id);
        const newIdx = Math.min(currentIdx, next.length - 1);
        setCurrentIdx(newIdx);
        return next;
      });
    },
    [currentIdx]
  );

  return {
    allIds,
    currentIdx,
    loadList,
    navTo,
    navFirst,
    navPrev,
    navNext,
    navLast,
    appendId,
    removeId,
  };
}
